/**
 * AddCartButton，加购按钮，点击元素本身触发
 *
 * Attributes:
 * @param {String} data-variant-id 产品变体 ID
 * @param {String} data-quantity 购买数量，可选，默认为1
 *
 * @example
 * ```html
 * <add-cart-button data-variant-id="{{ product.first_available_variant.id }}" data-quantity="1" class="button round">
 *   Add to cart
 *   {%- render 'loading-spinner' -%}
 * </add-cart-button>
 * ```
 */
if (!customElements.get('add-cart-button')) {
  class AddCartButton extends HTMLElement {
    // 标记
    _marks = {
      loading: 'loading',
    }

    _shopify_features_script = document.querySelector(
      "script[id='shopify-features']"
    )
    _shopify_features_json = JSON.parse(this._shopify_features_script.innerHTML)
    _checkout_headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      authorization: 'Basic ' + btoa(this._shopify_features_json.accessToken),
    }

    get _variantId() {
      return this.getAttribute('data-variant-id')
    }
    get _quantity() {
      return this.getAttribute('data-quantity') ?? 1
    }

    constructor() {
      super()
    }

    connectedCallback() {
      this.elements = {
        loadingSpinner: this.querySelector('.loading__spinner'),
      }
      this.addEventListener('click', (e) => {
        if (
          this.hasAttribute('disabled') ||
          this.getAttribute('aria-disabled') === 'true'
        ) {
          return
        }
        const variantIds = (this._variantId || '')
          .split(',')
          .map((id) => id.trim())
          .filter((id) => id)
        if (variantIds.length === 0) return

        this.startLoading()
        this.addToCart().finally(() => {
          this.finishLoading()
        })
      })
    }

    /**
     * 加购
     */
    async addToCart() {
      const variantIds = (this._variantId || '')
        .split(',')
        .map((id) => id.trim())
        .filter((id) => id)

      if (variantIds.length === 0) return

      const quantity = parseInt(this._quantity, 10) || 1
      const data_json =
        variantIds.length === 1
          ? {
              form_type: 'product',
              utf8: '✓',
              id: variantIds[0],
              quantity,
            }
          : {
              items: variantIds.map((id) => ({
                id: parseInt(id, 10),
                quantity,
              })),
            }

      return fetch('/cart/add.json', {
        method: 'POST',
        headers: this._checkout_headers,
        body: JSON.stringify(data_json),
      })
        .then((response) => {
          if (response.ok) {
            return response.json()
          }
          if (response.status === 422) {
            alert('Inventory shortage!')
          }
          throw new Error('Add to cart failed')
        })
        .then((response) => {
          return this.updateCart(response)
        })
        .catch((err) => {
          console.error(err)
        })
    }

    /**
     * 更新购物车，并弹出侧边购物车
     * @returns
     */
    // async updateCart(response) {
    //   publish(PUB_SUB_EVENTS.cartUpdate, {
    //     // source: 'product-form',
    //     productVariantId: this._variantId,
    //     cartData: response,
    //   })
    //   document.querySelector('cart-drawer').open()
    //   fetch(`${routes.cart_url}?section_id=header`)
    //     .then((response) => response.text())
    //     .then((responseText) => {
    //       const html = new DOMParser().parseFromString(
    //         responseText,
    //         'text/html'
    //       )
    //       const selectors = ['#cart-icon-bubble']
    //       for (const selector of selectors) {
    //         const targetElement = document.querySelector(selector)
    //         const sourceElement = html.querySelector(selector)
    //         if (targetElement && sourceElement) {
    //           targetElement.replaceWith(sourceElement)
    //         }
    //       }
    //     })
    //     .catch((e) => {
    //       console.error(e)
    //     })
    // }
    async updateCart(response) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        productVariantId: this._variantId,
        cartData: response,
      })

      // 先更新 cart-drawer 内容,再打开
      const cartDrawer = document.querySelector('cart-drawer')
      if (cartDrawer && typeof cartDrawer.setActiveElement === 'function') {
        cartDrawer.setActiveElement(document.activeElement)
      }

      fetch(`${routes.cart_url}?sections=cart-drawer,header`)
        .then((response) => response.json())
        .then((sections) => {
          // 更新 cart-drawer
          if (sections['cart-drawer']) {
            const html = new DOMParser().parseFromString(
              sections['cart-drawer'],
              'text/html'
            )
            const newDrawer = html.querySelector('cart-drawer')
            if (newDrawer) {
              cartDrawer.innerHTML = newDrawer.innerHTML
              cartDrawer.classList.remove('is-empty')
              // 重新绑定 overlay 点击事件
              const overlay = cartDrawer.querySelector('#CartDrawer-Overlay')
              if (overlay) {
                overlay.addEventListener(
                  'click',
                  cartDrawer.close.bind(cartDrawer)
                )
              }
            }
          }

          // 更新 header 图标
          if (sections['header']) {
            const headerHtml = new DOMParser().parseFromString(
              sections['header'],
              'text/html'
            )
            const selectors = ['#cart-icon-bubble']
            for (const selector of selectors) {
              const targetElement = document.querySelector(selector)
              const sourceElement = headerHtml.querySelector(selector)
              if (targetElement && sourceElement) {
                targetElement.replaceWith(sourceElement)
              }
            }
          }

          // 内容更新完成后打开抽屉
          cartDrawer.open()
        })
        .catch((e) => {
          console.error(e)
        })
    }

    startLoading() {
      this.setAttribute('aria-disabled', true)
      this.classList.add(this._marks.loading)
      this.elements.loadingSpinner?.classList.remove('hidden')
    }

    finishLoading() {
      this.removeAttribute('aria-disabled')
      this.classList.remove(this._marks.loading)
      this.elements.loadingSpinner?.classList.add('hidden')
    }
  }

  customElements.define('add-cart-button', AddCartButton)
}
